import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import Navbar from './Navbar';

const API_BASE_URL = 'https://infrec.in/php-backend/backend/user';
const ADMIN_API_URL = 'https://infrec.in/php-backend/backend/admin';

const CustomerProfile = () => {
  const [customer, setCustomer] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [allPolicies, setAllPolicies] = useState([]);
  const [activePoliciesCount, setActivePoliciesCount] = useState(0);
  const [inactivePoliciesCount, setInactivePoliciesCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const fetchCustomerProfile = async () => {
      try {
        const customerDataStr = sessionStorage.getItem('customerData');
        
        if (!customerDataStr) {
          console.log('No customer data in sessionStorage, redirecting to login');
          setError('Session expired. Please login again.');
          setTimeout(() => {
            navigate('/customer-login');
          }, 2000);
          return;
        }

        let customerData;
        try {
          customerData = JSON.parse(customerDataStr);
        } catch (parseErr) {
          console.error('Error parsing customer data:', parseErr);
          setError('Invalid session data. Please login again.');
          setTimeout(() => {
            navigate('/customer-login');
          }, 2000);
          return;
        }

        if (!customerData || !customerData.id) {
          console.log('Invalid customer data:', customerData);
          setError('Invalid customer data. Please login again.');
          setTimeout(() => {
            navigate('/customer-login');
          }, 2000);
          return;
        }

        console.log('Customer data found:', customerData);

        // Use sessionStorage data for customer profile
        setCustomer({
          id: customerData.id,
          name: customerData.name,
          email: customerData.email,
          mobile: customerData.mobile,
          customer_id: customerData.customer_id,
          unique_code: customerData.customer_id,
          date_of_birth: customerData.date_of_birth || '',
          address: customerData.address || '',
          profile_photo: customerData.profile_photo || null,
          created_at: customerData.created_at
        });

        // Fetch purchases from API
        try {
          console.log('Fetching policies for customer ID:', customerData.id);
          const response = await fetch(`${API_BASE_URL}/customerprofile.php?customer_id=${customerData.id}`);
          
          if (!response.ok) {
            console.error('API Error:', response.status, response.statusText);
            setPurchases([]);
            setLoading(false);
            return;
          }

          const text = await response.text();
          console.log('Raw API Response:', text);
          
          if (!text) {
            console.error('Empty response from API');
            setPurchases([]);
            setLoading(false);
            return;
          }

          const data = JSON.parse(text);
          console.log('Parsed API Response:', data);

          if (data.success) {
            console.log('Purchases fetched successfully:', data.purchases);
            console.log('Total policies:', data.purchases ? data.purchases.length : 0);
            setPurchases(data.purchases || []);
            if (data.customer && data.customers.profile_photo) {
              setCustomer(prev => ({
                ...prev,
                profile_photo: data.customers.profile_photo
              }));
            }
          } else {
            console.error('API Error:', data.message);
            setPurchases([]);
          }
        } catch (apiErr) {
          console.error('API Fetch Error:', apiErr);
          setPurchases([]);
        }

        // Fetch all policies from admin endpoint
        try {
          console.log('Fetching all policies from admin endpoint');
          console.log('Admin API URL:', `${ADMIN_API_URL}/get-all-policies.php`);
          
          const adminResponse = await fetch(`${ADMIN_API_URL}/get-all-policies.php`);
          console.log('Admin Response Status:', adminResponse.status);
          console.log('Admin Response OK:', adminResponse.ok);
          
          if (adminResponse.ok) {
            const adminData = await adminResponse.json();
            console.log('Admin Data Received:', adminData);
            console.log('Admin Data Success:', adminData.success);
            console.log('Admin Data Policies:', adminData.policies);
            console.log('Admin Data Active Count:', adminData.active_policies);
            console.log('Admin Data Inactive Count:', adminData.inactive_policies);
            console.log('Admin Data Total Policies:', adminData.total_policies);
            
            if (adminData.success) {
              console.log('Setting state with policies...');
              setAllPolicies(adminData.policies || []);
              setActivePoliciesCount(adminData.active_policies || 0);
              setInactivePoliciesCount(adminData.inactive_policies || 0);
              console.log('State updated successfully');
              console.log('Active:', adminData.active_policies, 'Inactive:', adminData.inactive_policies);
            } else {
              console.error('Admin API returned success: false');
              console.error('Error message:', adminData.message);
            }
          } else {
            console.error('Admin Response not OK');
            console.error('Response text:', await adminResponse.text());
          }
        } catch (adminErr) {
          console.error('Admin API Fetch Error:', adminErr);
          console.error('Error message:', adminErr.message);
          console.error('Error stack:', adminErr.stack);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError('Error loading profile. Please try again.');
        setLoading(false);
      }
    };

    fetchCustomerProfile();
  }, [navigate]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    console.log('File selected:', file.name, 'Type:', file.type, 'Size:', file.size);

    // Validate file type
    const allowed_types = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed_types.includes(file.type)) {
      alert('Invalid file type. Only JPEG, PNG, GIF, and WebP are allowed');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size exceeds 5MB limit');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('customer_id', customer.id);
      formData.append('photo', file);

      console.log('Uploading to customer ID:', customer.id);

      const uploadUrl = `${API_BASE_URL}/uploaddocument.php`;
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData
      });

      console.log('Response status:', response.status);

      const data = await response.json();

      console.log('Response data:', data);

      if (data.success) {
        setCustomer({...customer, profile_photo: data.photo_url});
        
        // Update sessionStorage
        const customerDataStr = sessionStorage.getItem('customerData');
        const customerData = JSON.parse(customerDataStr);
        customerData.profile_photo = data.photo_url;
        sessionStorage.setItem('customerData', JSON.stringify(customerData));

        alert('Profile photo updated successfully!');
      } else {
        alert('Error uploading photo: ' + data.message);
      }
    } catch (error) {
      console.error('Upload Error:', error);
      alert('Error uploading photo: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!customer.profile_photo) return;

    if (!window.confirm('Are you sure you want to delete your profile photo?')) {
      return;
    }

    setUploading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/deletepolicy.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_id: customer.id
        })
      });

      const data = await response.json();

      if (data.success) {
        setCustomer({...customer, profile_photo: null});
        
        // Update sessionStorage
        const customerDataStr = sessionStorage.getItem('customerData');
        const customerData = JSON.parse(customerDataStr);
        customerData.profile_photo = null;
        sessionStorage.setItem('customerData', JSON.stringify(customerData));

        alert('Profile photo deleted successfully!');
      } else {
        alert('Error deleting photo: ' + data.message);
      }
    } catch (error) {
      console.error('Delete Error:', error);
      alert('Error deleting photo: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md text-center">
          <p className="text-red-600 font-semibold mb-4">{error}</p>
          <button
            onClick={() => navigate('/customer-login')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">No customer data available</p>
      </div>
    );
  }

  // Calculate purchase statistics by category
  const productStats = purchases.reduce((acc, purchase) => {
    const categoryName = purchase.category_name;
    acc[categoryName] = (acc[categoryName] || 0) + 1;
    return acc;
  }, {});

  const totalAmount = purchases.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const maxCategory = Object.entries(productStats).sort((a, b) => b[1] - a[1])[0];

  // Sort purchases by date (most recent first)
  const recentlyBought = [...purchases].sort((a, b) => new Date(b.purchase_date) - new Date(a.purchase_date));

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar customerName={customer.name} />

      <div className="py-4 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Profile Image and Name Section */}
          <div className="mb-8 pt-12">
            <div className="flex flex-col items-center mb-8">
              {/* Profile Image Circle */}
              <div className="relative mb-4">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-32 h-32 rounded-full shadow-lg flex items-center justify-center text-6xl border-4 overflow-hidden hover:opacity-80 transition-opacity cursor-pointer"
                  style={{
                    backgroundColor: customer.profile_photo ? '#f0f0f0' : '#e8f0fe',
                    borderColor: '#305daa'
                  }}
                  title="Click to upload photo"
                >
                  {customer.profile_photo ? (
                    <img
                      src={customer.profile_photo}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    '👤'
                  )}
                </button>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfileImageUpload}
                  disabled={uploading}
                  className="hidden"
                />

                {customer.profile_photo && (
                  <button
                    onClick={handleDeletePhoto}
                    disabled={uploading}
                    className="absolute bottom-0 right-0 bg-red-600 rounded-full p-2 shadow-lg hover:shadow-xl transition-shadow"
                    title="Delete photo"
                  >
                    <FiTrash2 size={20} className="text-white" />
                  </button>
                )}

                {!customer.profile_photo && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="absolute bottom-0 right-0 rounded-full p-2 shadow-lg hover:shadow-xl transition-shadow"
                    style={{backgroundColor: '#305daa'}}
                    title="Upload photo"
                  >
                    <FiPlus size={20} className="text-white" />
                  </button>
                )}

                {uploading && (
                  <div className="absolute inset-0 rounded-full bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  </div>
                )}
              </div>

              {/* Customer Name */}
              <h1 className="text-4xl font-bold text-gray-900 mb-1">{customer.name}</h1>
              <p className="text-gray-600">Client ID: {customer.unique_code}</p>
            </div>
          </div>

          {/* Welcome Header */}
          <div className="mb-8">
            <div className="text-white rounded-lg shadow-md p-8" style={{backgroundColor: '#305daa'}}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="opacity-80 text-2xl mb-2">Welcome back,</p>
                  <p className="opacity-80 text-2xl">Member since {customer.member_since ? new Date(customer.member_since).toLocaleDateString() : 'N/A'}</p>
                </div>
                <div className="text-right">
                  <p className="opacity-80 text-lg">Today</p>
                  <p className="text-2xl font-semibold">{currentTime.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  <p className="opacity-80 text-2xl mt-2">{currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Customer Details Card */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{color: '#305daa'}}>Personal Information</h2>
            
            {/* Row 1: Full Name, Client ID, Email */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="border-l-4 pl-4" style={{borderColor: '#305daa'}}>
                <p className="text-gray-600 text-sm font-semibold uppercase">Full Name</p>
                <p className="text-lg font-semibold text-gray-900 mt-1">{customer.name}</p>
              </div>
              <div className="border-l-4 pl-4" style={{borderColor: '#305daa'}}>
                <p className="text-gray-600 text-sm font-semibold uppercase">Client ID</p>
                <p className="text-lg font-semibold text-gray-900 mt-1">{customer.unique_code}</p>
              </div>
              <div className="border-l-4 pl-4" style={{borderColor: '#305daa'}}>
                <p className="text-gray-600 text-sm font-semibold uppercase">Email</p>
                <p className="text-lg text-gray-900 mt-1">{customer.email}</p>
              </div>
            </div>

            {/* Row 2: Mobile Number, Date of Birth, Member Since */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="border-l-4 pl-4" style={{borderColor: '#305daa'}}>
                <p className="text-gray-600 text-sm font-semibold uppercase">Mobile Number</p>
                <p className="text-lg text-gray-900 mt-1">{customer.mobile}</p>
              </div>
              <div className="border-l-4 pl-4" style={{borderColor: '#305daa'}}>
                <p className="text-gray-600 text-sm font-semibold uppercase">Date of Birth</p>
                <p className="text-lg text-gray-900 mt-1">{customer.date_of_birth || 'N/A'}</p>
              </div>
              <div className="border-l-4 pl-4" style={{borderColor: '#305daa'}}>
                <p className="text-gray-600 text-sm font-semibold uppercase">Member Since</p>
                <p className="text-lg text-gray-900 mt-1">{customer.member_since ? new Date(customer.member_since).toLocaleDateString() : 'N/A'}</p>
              </div>
            </div>

            {/* Address Section */}
            <div className="border-t border-gray-200 mt-6 pt-6">
              <p className="text-gray-600 text-sm font-semibold uppercase mb-2">Address</p>
              <p className="text-lg text-gray-900">{customer.address || 'N/A'}</p>
            </div>
          </div>

          {/* Purchase Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow-md p-6 border-t-4" style={{borderColor: '#305daa'}}>
              <p className="text-gray-600 text-sm font-semibold uppercase">Total Products</p>
              <p className="text-4xl font-bold mt-2" style={{color: '#305daa'}}>{allPolicies.length}</p>
              <div className="mt-3 flex gap-4 text-xs">
                <span className="text-green-600 font-semibold">
                  ✓ Active: {activePoliciesCount}
                </span>
                <span className="text-red-600 font-semibold">
                  ✗ Inactive: {inactivePoliciesCount}
                </span>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 border-t-4" style={{borderColor: '#305daa'}}>
              <p className="text-gray-600 text-sm font-semibold uppercase">Total Investment</p>
              <p className="text-2xl font-bold mt-2" style={{color: '#305daa'}}>₹{totalAmount.toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 border-t-4" style={{borderColor: '#305daa'}}>
              <p className="text-gray-600 text-sm font-semibold uppercase">Active Products</p>
              <p className="text-4xl font-bold mt-2" style={{color: '#305daa'}}>{activePoliciesCount}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 border-t-4" style={{borderColor: '#305daa'}}>
              <p className="text-gray-600 text-sm font-semibold uppercase">Top Category</p>
              <p className="text-sm font-bold mt-2" style={{color: '#305daa'}}>{maxCategory ? maxCategory[0] : 'N/A'}</p>
            </div>
          </div>

          {/* Recently Bought Products */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-2xl font-bold mb-6" style={{color: '#305daa'}}>Recently Bought</h2>

            {recentlyBought.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600 text-lg">No products purchased yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {recentlyBought.map((purchase) => (
                  <div
                    key={purchase.id}
                    className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-l-4"
                    style={{borderColor: '#305daa'}}
                  >
                    <div className="text-5xl mb-4">{purchase.image}</div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {purchase.product_name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      <strong>Category:</strong> {purchase.category_name}
                    </p>
                    <p className="text-sm text-gray-600 mb-3">
                      <strong>Subcategory:</strong> {purchase.subcategory_name}
                    </p>
                    <p className="text-sm text-gray-600 mb-3">
                      <strong>Purchase Date:</strong> {new Date(purchase.purchase_date).toLocaleDateString()}
                    </p>
                    <div className="bg-gray-50 rounded-lg p-3 mb-4">
                      <p className="text-sm text-gray-600">Amount</p>
                      <p className="text-lg font-semibold" style={{color: '#305daa'}}>
                        ₹{parseFloat(purchase.amount).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </p>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      <strong>Policy #:</strong> {purchase.policy_number}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        purchase.status === 'Active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {purchase.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Policy Details Summary */}
          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{color: '#305daa'}}>Policy Details Summary</h2>
            {recentlyBought.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600 text-lg">No policy details available</p>
              </div>
            ) : (
              <div className="space-y-6">
                {recentlyBought.map((policy) => (
                  <div key={policy.id} className="border rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 border-b-4" style={{borderColor: '#305daa'}}>
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{policy.product_name}</h3>
                          <p className="text-sm text-gray-600 mt-1">Policy #: {policy.policy_number}</p>
                        </div>
                        <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                          policy.status && policy.status.toLowerCase() === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {policy.status || 'N/A'}
                        </span>
                      </div>
                    </div>

                    {/* Details Grid */}
                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="border-l-4 pl-4" style={{borderColor: '#305daa'}}>
                          <p className="text-gray-600 text-xs font-semibold uppercase">Category</p>
                          <p className="text-lg font-semibold text-gray-900 mt-2">{policy.category_name || 'N/A'}</p>
                        </div>
                        <div className="border-l-4 pl-4" style={{borderColor: '#305daa'}}>
                          <p className="text-gray-600 text-xs font-semibold uppercase">Subcategory</p>
                          <p className="text-lg font-semibold text-gray-900 mt-2">{policy.subcategory_name || 'N/A'}</p>
                        </div>
                        <div className="border-l-4 pl-4" style={{borderColor: '#305daa'}}>
                          <p className="text-gray-600 text-xs font-semibold uppercase">Nominee</p>
                          <p className="text-lg font-semibold text-gray-900 mt-2">{policy.nominee_name || 'N/A'}</p>
                        </div>
                        <div className="border-l-4 pl-4" style={{borderColor: '#305daa'}}>
                          <p className="text-gray-600 text-xs font-semibold uppercase">Sum Assured</p>
                          <p className="text-lg font-semibold" style={{color: '#305daa'}}>
                            ₹{parseFloat(policy.amount || 0).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </p>
                        </div>
                        <div className="border-l-4 pl-4" style={{borderColor: '#305daa'}}>
                          <p className="text-gray-600 text-xs font-semibold uppercase">Annual Premium</p>
                          <p className="text-lg font-semibold" style={{color: '#305daa'}}>
                            ₹{parseFloat(policy.annual_premium || 0).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </p>
                        </div>
                        <div className="border-l-4 pl-4" style={{borderColor: '#305daa'}}>
                          <p className="text-gray-600 text-xs font-semibold uppercase">Premium Frequency</p>
                          <p className="text-lg font-semibold text-gray-900 mt-2">{policy.premium_frequency || 'N/A'}</p>
                        </div>
                        <div className="border-l-4 pl-4" style={{borderColor: '#305daa'}}>
                          <p className="text-gray-600 text-xs font-semibold uppercase">Purchase Date</p>
                          <p className="text-lg font-semibold text-gray-900 mt-2">{new Date(policy.purchase_date).toLocaleDateString('en-IN')}</p>
                        </div>
                        <div className="border-l-4 pl-4" style={{borderColor: '#305daa'}}>
                          <p className="text-gray-600 text-xs font-semibold uppercase">Renewal Date</p>
                          <p className="text-lg font-semibold text-gray-900 mt-2">{policy.renewal_date ? new Date(policy.renewal_date).toLocaleDateString('en-IN') : 'N/A'}</p>
                        </div>
                        <div className="border-l-4 pl-4" style={{borderColor: '#305daa'}}>
                          <p className="text-gray-600 text-xs font-semibold uppercase">Maturity Date</p>
                          <p className="text-lg font-semibold text-gray-900 mt-2">{policy.maturity_date ? new Date(policy.maturity_date).toLocaleDateString('en-IN') : 'N/A'}</p>
                        </div>
                        <div className="border-l-4 pl-4" style={{borderColor: '#305daa'}}>
                          <p className="text-gray-600 text-xs font-semibold uppercase">Next Payment Date</p>
                          <p className="text-lg font-semibold text-gray-900 mt-2">{policy.next_payment_date ? new Date(policy.next_payment_date).toLocaleDateString('en-IN') : 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerProfile;
